import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, Building2, CalendarClock, Flame, Phone, PhoneCall, Plus, Search, Upload, Users } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useFetch } from "@/hooks/useFetch";
import { fromNow } from "@/lib/time";
import { Btn, SalesHeader, StageBadge, WarmthBadge } from "@/components/sales/ui";
import { AddCompanyDialog } from "@/components/sales/AddCompanyDialog";

const Stat = ({ label, value, icon: Icon, to, color, id }) => (
  <Link to={to} data-testid={`sales-stat-${id}`} className="rounded-2xl border bg-card/70 p-4 transition-colors hover:border-white/20">
    <Icon className="h-4 w-4" style={{ color }} />
    <p className="mt-4 font-display text-3xl font-bold">{value ?? "–"}</p>
    <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{label}</p>
  </Link>
);

export default function SalesHome() {
  const s = useFetch("/sales/summary");
  const nav = useNavigate();
  const [adding, setAdding] = useState(false);
  const next = async () => {
    try {
      const { data } = await api.get("/sales/next-lead");
      nav(`/sales/companies/${data.id}`);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const stats = [
    ["Assigned to me", s?.assigned, Users, "/sales/companies?owner=me", "#60a5fa", "assigned"],
    ["Calls logged today", s?.calls_today, Phone, "/sales/companies", "#a78bfa", "calls"],
    ["Conversations today", s?.conversations_today, PhoneCall, "/sales/companies", "#34d399", "conversations"],
    ["Follow-ups due", s?.followups_due, CalendarClock, "/sales/followups", "#fbbf24", "followups"],
    ["Interested", s?.interested, Building2, "/sales/companies?stage=interested", "#22d3ee", "interested"],
    ["Hot & warm", s?.hot, Flame, "/sales/companies?warmth=Hot", "#ef4444", "hot"],
    ["Uncontacted", s?.uncontacted, Building2, "/sales/companies", "#94a3b8", "uncontacted"],
    ["Need attention", s?.attention, CalendarClock, "/sales/companies?followup=due", "#fb923c", "attention"],
    ["With Insight calls", s?.with_insight, PhoneCall, "/sales/companies?insight=1", "#10b981", "insight"],
  ];
  return (
    <div data-testid="sales-home">
      <SalesHeader title="Sales workspace" subtitle="Find a company, understand it, call, record the outcome, move on.">
        <Btn variant="cyan" onClick={next} data-testid="find-next-lead-btn"><ArrowRight className="h-4 w-4" /> Find Next Lead</Btn>
        <Btn onClick={() => setAdding(true)} data-testid="add-company-btn"><Plus className="h-4 w-4" /> Add Company</Btn>
        <Btn onClick={() => nav("/sales/import")} data-testid="import-leads-btn"><Upload className="h-4 w-4" /> Import Leads</Btn>
        <Btn onClick={() => nav("/sales/companies")} data-testid="search-companies-btn"><Search className="h-4 w-4" /> Search</Btn>
      </SalesHeader>
      <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-5">{stats.map(([l, v, i, to, c, id]) => <Stat key={id} label={l} value={v} icon={i} to={to} color={c} id={id} />)}</div>
      <section className="mt-8 rounded-2xl border bg-card/70 p-5" data-testid="recent-opportunities">
        <h2 className="mb-4 font-display text-lg font-semibold">Recently updated</h2>
        {!s ? <div className="h-20 animate-pulse rounded-xl bg-white/[0.04]" /> : s.recent.length === 0 ? (
          <p className="text-sm text-muted-foreground">No companies yet. Import a CSV or add one to get started.</p>
        ) : s.recent.map((c) => (
          <Link key={c.id} to={`/sales/companies/${c.id}`} className="flex items-center gap-3 border-b py-3 last:border-0 hover:text-cyan-300">
            <span className="flex-1 truncate font-medium">{c.name}</span>
            <StageBadge stage={c.stage} /><WarmthBadge w={c.warmth} />
            <span className="hidden w-28 text-right text-xs text-muted-foreground sm:block">{fromNow(c.updated_at)}</span>
          </Link>
        ))}
      </section>
      <AddCompanyDialog open={adding} onClose={() => setAdding(false)} onCreated={(c) => nav(`/sales/companies/${c.id}`)} />
    </div>
  );
}
